import { useState, useEffect } from 'react';
import { useLocation } from 'wouter';
import { Book, Trash2, Play, Clock, CheckCircle, AlertCircle } from 'lucide-react';
import { api } from '../lib/api';
import BottomNav from '../components/BottomNav';

interface Book {
  id: string;
  title: string;
  author: string;
  total_pages: number;
  generation_mode: string;
  status: string;
  progress: number;
  created_at: string;
}

export default function MyBooks() {
  const [, setLocation] = useLocation();
  const [books, setBooks] = useState<Book[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadBooks();
  }, []);

  const loadBooks = async () => {
    try {
      const response = await api.getBooks();
      setBooks(response.data || []);
    } catch (error) {
      console.error('加载书籍失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('确定要删除这本书吗?')) return;

    try {
      await api.deleteBook(id);
      setBooks(books.filter(b => b.id !== id));
    } catch (error) {
      alert('删除失败');
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-5 h-5 text-green-400" />;
      case 'processing':
        return <Clock className="w-5 h-5 text-[#22a9d9] animate-spin" />;
      case 'failed':
        return <AlertCircle className="w-5 h-5 text-red-400" />;
      default:
        return <Clock className="w-5 h-5 text-white/40" />;
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'completed':
        return '已完成';
      case 'processing':
        return '处理中';
      case 'failed':
        return '失败';
      default:
        return '等待中';
    }
  };

  return (
    <div className="min-h-screen bg-[#1a2e3e] pb-24">
      {/* 头部 */}
      <div className="p-6">
        <h1 className="text-3xl font-bold text-white mb-2">我的书籍</h1>
        <p className="text-white/60">共 {books.length} 本书籍</p>
      </div>

      {/* 书籍列表 */}
      <div className="px-6 space-y-4">
        {loading ? (
          <div className="text-center py-12 text-white/60">
            加载中...
          </div>
        ) : books.length === 0 ? (
          <div className="text-center py-12">
            <Book className="w-16 h-16 text-white/20 mx-auto mb-4" />
            <p className="text-white/60 mb-6">还没有上传任何书籍</p>
            <button
              onClick={() => setLocation('/upload')}
              className="px-8 h-12 rounded-xl bg-gradient-to-r from-[#22a9d9] to-[#6c5ce7] text-white font-medium hover:shadow-glow transition-shadow"
            >
              上传第一本书
            </button>
          </div>
        ) : (
          books.map((book) => (
            <div key={book.id} className="glass rounded-2xl p-4">
              <div className="flex gap-4">
                {/* 书籍封面 */}
                <div className="w-16 h-20 rounded-xl bg-gradient-to-br from-[#22a9d9] to-[#6c5ce7] flex items-center justify-center flex-shrink-0">
                  <Book className="w-8 h-8 text-white" />
                </div>

                {/* 书籍信息 */}
                <div className="flex-1 min-w-0">
                  <h3 className="text-white font-medium truncate mb-1">
                    {book.title}
                  </h3>
                  <p className="text-white/60 text-sm mb-2">{book.author}</p>
                  
                  {/* 状态 */}
                  <div className="flex items-center gap-2 mb-2">
                    {getStatusIcon(book.status)}
                    <span className="text-sm text-white/70">
                      {getStatusText(book.status)}
                    </span>
                    {book.status === 'processing' && (
                      <span className="text-sm text-white/50">
                        {book.progress}%
                      </span>
                    )}
                  </div>

                  {/* 进度条 */}
                  {book.status === 'processing' && (
                    <div className="h-1 bg-white/10 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-[#22a9d9] to-[#6c5ce7] transition-all duration-300"
                        style={{ width: `${book.progress}%` }}
                      />
                    </div>
                  )}
                </div>

                {/* 操作按钮 */}
                <div className="flex flex-col gap-2">
                  {book.status === 'completed' && (
                    <button
                      onClick={() => setLocation(`/cards?bookId=${book.id}`)}
                      className="w-10 h-10 rounded-xl bg-[#22a9d9]/20 flex items-center justify-center hover:bg-[#22a9d9]/30 transition-colors"
                    >
                      <Play className="w-5 h-5 text-[#22a9d9]" />
                    </button>
                  )}
                  <button
                    onClick={() => handleDelete(book.id)}
                    className="w-10 h-10 rounded-xl bg-red-500/20 flex items-center justify-center hover:bg-red-500/30 transition-colors"
                  >
                    <Trash2 className="w-5 h-5 text-red-400" />
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      <BottomNav />
    </div>
  );
}
